import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";

import AppShell from "./components/AppShell";
import FeedbackWidget from "./components/FeedbackWidget";
import FoundingLanding from "./landing/FoundingLanding";
import AdminApp from "./admin/AdminApp";

import StepRenderer from "./screens/StepRenderer";
import Login from "./screens/Login";
import SignUp from "./screens/SignUp";
import ForgotPassword from "./screens/ForgotPassword";
import ResetPassword from "./screens/ResetPassword";
import AuthCallback from "./screens/AuthCallback";
import Privacy from "./screens/Privacy";
import Terms from "./screens/Terms";
import Tiers from "./screens/Tiers";
import Settings from "./screens/Settings";
import DeleteAccountRequest from "./screens/DeleteAccountRequest";
import PaymentSuccess from "./screens/PaymentSuccess";
import PaymentCancel from "./screens/PaymentCancel";

import { useOnboardingFlow } from "./hooks/useOnboardingFlow";
import { AccountProvider } from "./account/AccountContext";

function BootLoader() {
    return (
        <div className="flex-1 flex items-center justify-center">
            <motion.div
                className="w-12 h-12 rounded-full bg-gradient-to-br from-rose to-gold"
                animate={{ scale: [1, 1.08, 1], opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 1.6, repeat: Infinity }}
            />
        </div>
    );
}

function BootError({ message }) {
    return (
        <div className="flex-1 flex items-center justify-center px-7 text-center">
            <p className="font-display italic text-2xl text-burgundy">{message}</p>
        </div>
    );
}

function OnboardingApp() {
    const flow = useOnboardingFlow();

    let body;
    if (flow.bootError) body = <BootError message={flow.bootError} />;
    else if (!flow.user) body = <BootLoader />;
    else body = <StepRenderer flow={flow} />;

    return (
        <AppShell>
            <FeedbackWidget />
            <AnimatePresence mode="wait">
                <motion.div
                    key={flow.step}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="flex-1 flex flex-col"
                >
                    {body}
                </motion.div>
            </AnimatePresence>
        </AppShell>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <Toaster position="top-center" />
            <AccountProvider>
                <Routes>
                    <Route path="/admin/*" element={<AdminApp />} />
                    <Route path="/founding" element={<FoundingLanding />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<SignUp />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/privacy-policy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/terms-and-conditions" element={<Terms />} />
                    <Route path="/tiers" element={<Tiers />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/delete-account" element={<DeleteAccountRequest />} />
                    <Route path="/payment/success" element={<PaymentSuccess />} />
                    <Route path="/payment/cancel" element={<PaymentCancel />} />
                    <Route path="*" element={<OnboardingApp />} />
                </Routes>
            </AccountProvider>
        </BrowserRouter>
    );
}
